import type { Collider } from "./types";

export interface Point2 {
  x: number;
  z: number;
}

const CONTACT_EPSILON = 0.0005;

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

export function pointInsideAabb(
  x: number,
  z: number,
  collider: Collider,
  padding = 0,
): boolean {
  return (
    x >= collider.minX - padding &&
    x <= collider.maxX + padding &&
    z >= collider.minZ - padding &&
    z <= collider.maxZ + padding
  );
}

/**
 * Tests the player's circular footprint against a rectangular world collider.
 * Unlike the previous square-vs-square test, corners remain genuinely rounded,
 * so the player can slide past furniture without catching on invisible corners.
 */
export function circleIntersectsAabb(
  x: number,
  z: number,
  radius: number,
  collider: Collider,
): boolean {
  const closestX = clamp(x, collider.minX, collider.maxX);
  const closestZ = clamp(z, collider.minZ, collider.maxZ);
  const dx = x - closestX;
  const dz = z - closestZ;
  const safeRadius = Math.max(0, radius - CONTACT_EPSILON);
  return dx * dx + dz * dz < safeRadius * safeRadius;
}

export function positionIsBlocked(
  x: number,
  z: number,
  radius: number,
  bounds: Collider,
  colliders: readonly Collider[],
): boolean {
  if (
    x - radius < bounds.minX ||
    x + radius > bounds.maxX ||
    z - radius < bounds.minZ ||
    z + radius > bounds.maxZ
  ) {
    return true;
  }

  return colliders.some((collider) =>
    circleIntersectsAabb(x, z, radius, collider),
  );
}

/**
 * Moves a circle through the world in short swept steps. Each blocked step is
 * split by axis to preserve wall sliding while preventing high-speed tunnelling.
 */
export function moveCircleWithSliding(
  start: Point2,
  delta: Point2,
  radius: number,
  bounds: Collider,
  colliders: readonly Collider[],
): Point2 {
  let x = start.x;
  let z = start.z;
  const distance = Math.hypot(delta.x, delta.z);
  const maxStep = Math.max(0.08, radius * 0.42);
  const steps = Math.max(1, Math.ceil(distance / maxStep));
  const stepX = delta.x / steps;
  const stepZ = delta.z / steps;

  for (let index = 0; index < steps; index += 1) {
    if (
      !positionIsBlocked(
        x + stepX,
        z + stepZ,
        radius,
        bounds,
        colliders,
      )
    ) {
      x += stepX;
      z += stepZ;
      continue;
    }

    const xFirst = Math.abs(stepX) >= Math.abs(stepZ);
    const slide = (axis: "x" | "z"): void => {
      const nextX = axis === "x" ? x + stepX : x;
      const nextZ = axis === "z" ? z + stepZ : z;
      if (!positionIsBlocked(nextX, nextZ, radius, bounds, colliders)) {
        x = nextX;
        z = nextZ;
      }
    };

    slide(xFirst ? "x" : "z");
    slide(xFirst ? "z" : "x");
  }

  return { x, z };
}

/**
 * Pushes a circle out of a box it has already sunk into, along the shortest
 * escape. Returns null when there is nothing to resolve.
 *
 * Static walls never need this — the player can't walk into them. Moving cars
 * can: they sweep onto a standing player, and `positionIsBlocked` then reports
 * every direction as blocked, which would pin the player until the car leaves.
 */
export function pushCircleOutOfAabb(
  x: number,
  z: number,
  radius: number,
  collider: Collider,
): Point2 | null {
  const closestX = clamp(x, collider.minX, collider.maxX);
  const closestZ = clamp(z, collider.minZ, collider.maxZ);
  const dx = x - closestX;
  const dz = z - closestZ;
  const distanceSquared = dx * dx + dz * dz;

  if (distanceSquared >= radius * radius) return null;

  if (distanceSquared > 1e-10) {
    const distance = Math.sqrt(distanceSquared);
    const push = radius - distance + CONTACT_EPSILON;
    return { x: x + (dx / distance) * push, z: z + (dz / distance) * push };
  }

  // Dead centre: no direction is "outwards" any more, so leave by the nearest face.
  const toMinX = x - collider.minX;
  const toMaxX = collider.maxX - x;
  const toMinZ = z - collider.minZ;
  const toMaxZ = collider.maxZ - z;
  const shortest = Math.min(toMinX, toMaxX, toMinZ, toMaxZ);
  const escape = radius + CONTACT_EPSILON;

  if (shortest === toMinX) return { x: collider.minX - escape, z };
  if (shortest === toMaxX) return { x: collider.maxX + escape, z };
  if (shortest === toMinZ) return { x, z: collider.minZ - escape };
  return { x, z: collider.maxZ + escape };
}

/**
 * Resolves every overlap in one settling pass per iteration, then clamps the
 * result back inside the world. Two iterations are enough for the corner case
 * where escaping one box sinks the circle into its neighbour.
 */
export function resolvePenetration(
  point: Point2,
  radius: number,
  bounds: Collider,
  colliders: readonly Collider[],
  iterations = 2,
): Point2 {
  let { x, z } = point;
  const touched: Collider[] = [];

  for (let pass = 0; pass < iterations; pass += 1) {
    let moved = false;
    for (const collider of colliders) {
      const escape = pushCircleOutOfAabb(x, z, radius, collider);
      if (!escape) continue;
      if (!touched.includes(collider)) touched.push(collider);
      x = escape.x;
      z = escape.z;
      moved = true;
    }
    if (!moved) break;
  }

  // Coincé entre deux corps plus rapprochés que sa propre largeur, le joueur se
  // fait renvoyer de l'un à l'autre à chaque passe. Dans ce cas seulement, on
  // sort de l'enveloppe de tout ce qui l'a repoussé : aucune position entre les
  // deux n'existe, et rester bloqué est pire que sortir un peu loin.
  const stuck = colliders.some((collider) =>
    circleIntersectsAabb(x, z, radius, collider),
  );
  if (stuck && touched.length) {
    const envelope = touched.reduce<Collider>(
      (box, collider) => ({
        minX: Math.min(box.minX, collider.minX),
        maxX: Math.max(box.maxX, collider.maxX),
        minZ: Math.min(box.minZ, collider.minZ),
        maxZ: Math.max(box.maxZ, collider.maxZ),
      }),
      { ...touched[0] },
    );
    const escape = pushCircleOutOfAabb(x, z, radius, envelope);
    if (escape) {
      x = escape.x;
      z = escape.z;
    }
  }

  return {
    x: clamp(x, bounds.minX + radius, bounds.maxX - radius),
    z: clamp(z, bounds.minZ + radius, bounds.maxZ - radius),
  };
}

/**
 * Returns true when a wall or prop crosses the open segment between the player
 * and an interaction anchor. The ends are ignored to avoid the target blocking
 * its own prompt.
 */
export function segmentCrossesAabb(
  from: Point2,
  to: Point2,
  collider: Collider,
  padding = 0.03,
): boolean {
  const minX = collider.minX - padding;
  const maxX = collider.maxX + padding;
  const minZ = collider.minZ - padding;
  const maxZ = collider.maxZ + padding;
  const dx = to.x - from.x;
  const dz = to.z - from.z;
  let entry = 0;
  let exit = 1;

  const clip = (origin: number, direction: number, min: number, max: number): boolean => {
    if (Math.abs(direction) < 1e-8) {
      return origin >= min && origin <= max;
    }
    const inverse = 1 / direction;
    let near = (min - origin) * inverse;
    let far = (max - origin) * inverse;
    if (near > far) [near, far] = [far, near];
    entry = Math.max(entry, near);
    exit = Math.min(exit, far);
    return entry <= exit;
  };

  if (!clip(from.x, dx, minX, maxX) || !clip(from.z, dz, minZ, maxZ)) {
    return false;
  }

  return entry > 0.015 && entry < 0.965;
}
